import { useRef } from 'react'
import { useVisemeEnvelope } from '../useVisemeEnvelope'
import { useVizCanvas } from '../useVizCanvas'
import { withAlpha } from '../vizColor'

// Reactive orb: a glowing core whose radius pulses with the envelope and whose
// perimeter wobbles (a few summed sine lobes) so vowels make it bloom and
// distort. Expanding rings are emitted when the amplitude spikes. Ambient and
// character-y — reads as "this thing is alive and talking".
export function OrbViz({ character, color, anyVoice }) {
  const { read, activeRef, reduceMotion } = useVisemeEnvelope(character, { anyVoice })
  const ringsRef = useRef([]) // {r, life}
  const prevRef = useRef(0)

  const draw = (ctx, { w, h, amp, t }) => {
    const cx = w / 2
    const cy = h / 2
    const baseR = Math.min(w, h) * 0.18
    const r = baseR * (1 + amp * 1.1)

    // Emit a ring on a rising amplitude edge (a new loud phoneme).
    if (amp - prevRef.current > 0.18) ringsRef.current.push({ r, life: 1 })
    prevRef.current = amp
    const rings = ringsRef.current
    for (const ring of rings) {
      ring.r += 2.4
      ring.life -= 0.025
    }
    ringsRef.current = rings.filter((ri) => ri.life > 0)

    // Rings.
    for (const ring of ringsRef.current) {
      ctx.strokeStyle = withAlpha(color, ring.life * 0.5)
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.arc(cx, cy, ring.r, 0, Math.PI * 2)
      ctx.stroke()
    }

    // Outer glow halo.
    const halo = ctx.createRadialGradient(cx, cy, r * 0.2, cx, cy, r * 2.4)
    halo.addColorStop(0, withAlpha(color, 0.35 + amp * 0.35))
    halo.addColorStop(1, withAlpha(color, 0))
    ctx.fillStyle = halo
    ctx.beginPath()
    ctx.arc(cx, cy, r * 2.4, 0, Math.PI * 2)
    ctx.fill()

    // Wobbling blob body: radius modulated by summed lobes around the circle.
    ctx.beginPath()
    const STEPS = 64
    for (let i = 0; i <= STEPS; i++) {
      const ang = (i / STEPS) * Math.PI * 2
      const wob =
        1 +
        amp *
          (0.12 * Math.sin(ang * 3 + t * 0.08) +
            0.08 * Math.sin(ang * 5 - t * 0.05))
      const rr = r * wob
      const x = cx + Math.cos(ang) * rr
      const y = cy + Math.sin(ang) * rr
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)
    }
    ctx.closePath()
    const body = ctx.createRadialGradient(cx, cy, 0, cx, cy, r)
    body.addColorStop(0, withAlpha(color, 0.95))
    body.addColorStop(1, withAlpha(color, 0.25))
    ctx.fillStyle = body
    ctx.shadowColor = color
    ctx.shadowBlur = 16
    ctx.fill()
    ctx.shadowBlur = 0
  }

  const ref = useVizCanvas({ read, activeRef, reduceMotion, draw })
  return <canvas ref={ref} style={{ display: 'block', width: '100%', height: '100%' }} />
}
